import type { ReactNode } from 'react';

import {
  AGENT_PANEL_MAX_WIDTH_PX,
  AGENT_PANEL_MIN_WIDTH_PX,
} from 'atoms/agent-chat-atoms';
import { HorizontalDraggableDivider } from 'components/draggable-divider';
import style from './agent-dock.module.scss';

/**
 * Right-hand dock the agent chat panel lives in: a fixed-position rail with a
 * draggable left edge. Shared by every page that hosts an agent so the panel
 * looks and resizes the same everywhere.
 *
 * The dock is `position: fixed`, so it does not reserve layout space; the host
 * page pads its own content by `width` while open.
 * @param {object} props The dock.
 * @param {number} props.width The dock's width in pixels.
 * @param {Function} props.onResize Called with the new width while dragging.
 * @param {ReactNode} props.children The panel.
 * @return {JSX.Element} The dock.
 */
export function AgentDock({
  width,
  onResize,
  children,
}: {
  width: number;
  onResize: (widthPx: number) => void;
  children: ReactNode;
}) {
  return (
    <aside className={style.agentDock} style={{ width }}>
      <HorizontalDraggableDivider
        minWidth={AGENT_PANEL_MIN_WIDTH_PX}
        maxWidth={AGENT_PANEL_MAX_WIDTH_PX}
        onDrag={onResize}
        value={width}
      />
      <div className={style.agentDockBody}>{children}</div>
    </aside>
  );
}
